import { useAccounts } from '@api/hooks';
import {
	DateStringPicker,
	SelectInput,
	TextArea,
	TextInput,
} from '@gurupay/gp-ui-library';
import { useDebounce } from '@hooks/useDebounce';
import { startOfDay } from 'date-fns';
import { enUS, lt } from 'date-fns/locale';
import { useState } from 'react';
import {
	type Control,
	Controller,
	type FieldErrors,
	get,
	type UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AccountStatus } from 'types/operations.ts';

import { type ConfigProps, rulesFor } from './utils';

interface DefaultFormProps {
	topic?: string | undefined;
	content?:
		| {
				text?: string | undefined;
		  }
		| undefined;
}

interface DynamicFieldsComponentProps {
	path: string;
	cfg: ConfigProps;
	errors: FieldErrors<DefaultFormProps>;
	signingInitiated: boolean;
	control: Control<any>;
	setValue: UseFormSetValue<any>;
}

const DEBOUNCE_TIME = 300;
const today = startOfDay(new Date());

const DynamicFields = ({
	path,
	cfg,
	errors,
	signingInitiated,
	control,
	setValue,
}: DynamicFieldsComponentProps) => {
	const { t, i18n } = useTranslation('', { keyPrefix: 'messages' });
	const [ibanSearchParam, setIbanSearchParam] = useState('');
	const debouncedIban = useDebounce(ibanSearchParam, DEBOUNCE_TIME);
	const { data: accounts, isLoading: isIbansLoading } = useAccounts({
		status: [AccountStatus.OPENED],
		limit: 20,
		iban: debouncedIban,
	});
	const ibanArray =
		accounts?.items?.map((acc) => ({ label: acc.iban, value: acc.iban })) ?? [];
	const fieldError = get(errors, path);
	const transformedOptions = cfg?.options?.map((option) => ({
		value: option.value,
		label: t(`options.${option.key}`),
	}));

	switch (cfg.type) {
		case 'text':
			return (
				<Controller
					key={path}
					control={control}
					name={path}
					render={({ field }) => (
						<TextArea
							key={`${path}-${cfg.label}`}
							error={fieldError?.message}
							{...field}
							id={`${path}-${cfg.label}`}
							isDisabled={signingInitiated}
							isRequired={!!cfg.required}
							label={t(`${cfg.label}`)}
						/>
					)}
					rules={rulesFor(cfg, t)}
				/>
			);
		case 'email':
			return (
				<Controller
					key={path}
					control={control}
					name={path}
					render={({ field }) => (
						<TextInput
							type="email"
							{...field}
							error={fieldError?.message}
							isDisabled={signingInitiated}
							isRequired={cfg.required}
							label={t(`${cfg.label}`)}
						/>
					)}
					rules={rulesFor(cfg, t)}
				/>
			);
		case 'phone':
			return (
				<Controller
					key={path}
					control={control}
					name={path}
					render={({ field }) => (
						<TextInput
							type="tel"
							{...field}
							error={fieldError?.message}
							isDisabled={signingInitiated}
							isRequired={!!cfg.required}
							label={t(`${cfg.label}`)}
						/>
					)}
					rules={rulesFor(cfg, t)}
				/>
			);
		case 'string':
			return (
				<Controller
					key={path}
					control={control}
					name={path}
					render={({ field }) => (
						<TextInput
							{...field}
							error={fieldError?.message}
							isDisabled={signingInitiated}
							isRequired={!!cfg.required}
							label={t(`${cfg.label}`)}
						/>
					)}
					rules={rulesFor(cfg, t)}
				/>
			);
		case 'select':
			return (
				<Controller
					control={control}
					name={path}
					render={({ field }) => {
						const { onChange, value, ...rest } = field;
						return (
							<SelectInput
								{...rest}
								error={fieldError?.message}
								isDisabled={signingInitiated}
								isRequired={!!cfg.required}
								label={t(`${cfg.label}`)}
								onChange={(val: string) => {
									onChange(val);
									setValue(field.name, val, {
										shouldDirty: true,
										shouldTouch: true,
										shouldValidate: true,
									});
								}}
								options={transformedOptions!}
								placeholder={cfg.placeholder}
								value={cfg.value ?? value}
							/>
						);
					}}
					rules={rulesFor(cfg, t)}
				/>
			);
		case 'iban':
			return (
				<Controller
					key={path}
					control={control}
					name={path}
					render={({ field }) => {
						const { onChange, value, ...rest } = field;
						return (
							<SelectInput
								{...rest}
								error={fieldError?.message}
								isDisabled={signingInitiated}
								isLoading={isIbansLoading}
								isRequired={!!cfg.required}
								label={t(`${cfg.label}`)}
								onChange={(val: string) => {
									onChange(val);
									setValue(field.name, val, {
										shouldDirty: true,
										shouldTouch: true,
										shouldValidate: true,
									});
								}}
								onSearch={setIbanSearchParam}
								options={ibanArray}
								placeholder={cfg.placeholder}
								showClear
								value={cfg.value ?? value}
							/>
						);
					}}
					rules={rulesFor(cfg, t)}
				/>
			);
		case 'date':
			return (
				<Controller
					control={control}
					name={path}
					render={({ field }) => (
						<DateStringPicker
							{...field}
							allowClear
							disabledFuture
							error={fieldError?.message}
							isDisabled={signingInitiated}
							label={t(`${cfg.label}`)}
							locale={i18n.language === 'lt' ? lt : enUS}
							startDate={today}
							startMonth={new Date(2015, 1)}
						/>
					)}
				/>
			);

		default:
			return null;
	}
};

export default DynamicFields;
